import React, { useState, useEffect } from 'react'
import { dbOperations } from '../../utils/supabase'
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  TagIcon,
  EyeIcon,
  EyeSlashIcon,
  PhotoIcon,
  XMarkIcon,
  CloudArrowUpIcon
} from '@heroicons/react/24/outline'

const CategoriesPage = () => {
  const [categories, setCategories] = useState([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [editingCategory, setEditingCategory] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')

  // Görsel yükleme state'leri
  const [selectedFiles, setSelectedFiles] = useState([])
  const [uploadedImages, setUploadedImages] = useState([])
  const [uploadingImages, setUploadingImages] = useState(false)

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    image_url: '',
    is_active: true
  })

  useEffect(() => {
    loadCategories()
  }, [])

  // Dosya seçimi
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files)
    setSelectedFiles(files)
  }

  // Seçili dosyaları kaldır
  const removeSelectedFile = (index) => {
    setSelectedFiles(selectedFiles.filter((_, i) => i !== index))
  }

  // Yüklenmiş resmi kaldır
  const removeUploadedImage = async (index) => {
    try {
      const image = uploadedImages[index]
      if (image.fileName) {
        await dbOperations.storage.deleteFile(image.fileName)
      }
      setUploadedImages(uploadedImages.filter((_, i) => i !== index))
      
      // Eğer silinen resim ana resimse formData'dan da kaldır
      if (formData.image_url === image.url) {
        setFormData(prev => ({ ...prev, image_url: '' }))
      }
    } catch (error) {
      console.error('Error deleting image:', error)
    }
  }

  // Resimleri yükleme
  const uploadImages = async () => {
    if (selectedFiles.length === 0) return

    setUploadingImages(true)
    try {
      const uploadPromises = selectedFiles.map(file => 
        dbOperations.storage.uploadImage(file)
      )
      
      const results = await Promise.all(uploadPromises)
      
      const successfulUploads = results
        .filter(result => !result.error)
        .map(result => ({
          url: result.url,
          fileName: result.fileName
        }))

      const newUploadedImages = [...uploadedImages, ...successfulUploads]
      setUploadedImages(newUploadedImages)
      setSelectedFiles([])

      // Ana resim URL'sini güncelle (ilk yüklenen resim)
      if (successfulUploads.length > 0 && !formData.image_url) {
        const newImageUrl = successfulUploads[0].url
        setFormData(prev => ({
          ...prev,
          image_url: newImageUrl
        }))
      }
    } catch (error) {
      console.error('Error uploading images:', error)
    } finally {
      setUploadingImages(false)
    }
  }

  const loadCategories = async () => {
    try {
      setLoading(true)
      const { data } = await dbOperations.categories.getAll()
      setCategories(data || [])
    } catch (error) {
      console.error('Error loading categories:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    try {
      let finalImageUrl = formData.image_url

      // Eğer seçili dosyalar varsa önce onları yükle
      if (selectedFiles.length > 0) {
        setUploadingImages(true)
        try {
          const uploadPromises = selectedFiles.map(file => 
            dbOperations.storage.uploadImage(file)
          )
          
          const results = await Promise.all(uploadPromises)
          
          const successfulUploads = results
            .filter(result => !result.error)
            .map(result => ({
              url: result.url,
              fileName: result.fileName
            }))

          if (successfulUploads.length > 0 && !finalImageUrl) {
            finalImageUrl = successfulUploads[0].url
          }
        } catch (error) {
          console.error('Error uploading images:', error)
        } finally {
          setUploadingImages(false)
        }
      }

      const categoryData = {
        ...formData,
        image_url: finalImageUrl
      }

      if (editingCategory) {
        const { data } = await dbOperations.categories.update(editingCategory.id, categoryData)
        setCategories(categories.map(c => c.id === editingCategory.id ? data : c))
      } else {
        const { data } = await dbOperations.categories.create(categoryData)
        setCategories([data, ...categories])
      }

      resetForm()
      setShowModal(false)
    } catch (error) {
      console.error('Error saving category:', error)
    }
  }

  const handleEdit = (category) => {
    setEditingCategory(category)
    setFormData({
      name: category.name,
      description: category.description || '',
      image_url: category.image_url || '',
      is_active: category.is_active
    })
    setShowModal(true)
  }

  const handleDelete = async (categoryId) => {
    if (window.confirm('Bu kategoriyi silmek istediğinizden emin misiniz?')) {
      try {
        await dbOperations.categories.delete(categoryId)
        setCategories(categories.filter(c => c.id !== categoryId))
      } catch (error) {
        console.error('Error deleting category:', error)
      }
    }
  }

  const toggleActive = async (category) => {
    try {
      const { data } = await dbOperations.categories.update(category.id, {
        is_active: !category.is_active
      })
      setCategories(categories.map(c => c.id === category.id ? data : c))
    } catch (error) {
      console.error('Error updating category status:', error)
    }
  }

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      image_url: '',
      is_active: true
    })
    setEditingCategory(null)
    setSelectedFiles([])
    setUploadedImages([])
  }

  const filteredCategories = categories.filter(category => 
    category.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    category.description?.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Başlık ve Ekle Butonu */}
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-bold text-gray-900">Kategoriler</h1>
          <p className="mt-2 text-sm text-gray-700">
            Ürün kategorilerini görüntüleyin, düzenleyin ve yeni kategori ekleyin.
          </p>
        </div>
        <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
          <button
            type="button"
            onClick={() => {
              resetForm()
              setShowModal(true)
            }}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            Yeni Kategori
          </button>
        </div>
      </div>

      {/* Arama */}
      <div className="bg-white p-4 rounded-lg shadow">
        <input
          type="text"
          placeholder="Kategori adı veya açıklama ile ara..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
        />
      </div>

      {/* Kategori Grid */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        {filteredCategories.length === 0 ? (
          <div className="text-center py-12">
            <TagIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">Kategori bulunamadı</h3>
            <p className="mt-1 text-sm text-gray-500">
              Arama kriterlerinize uygun kategori bulunmuyor.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 p-6">
            {filteredCategories.map((category) => (
              <div key={category.id} className="bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow">
                <div className="aspect-w-16 aspect-h-9 rounded-t-lg overflow-hidden">
                  {category.image_url ? (
                    <img
                      className="w-full h-48 object-cover"
                      src={category.image_url}
                      alt={category.name}
                    />
                  ) : (
                    <div className="w-full h-48 bg-gray-100 flex items-center justify-center">
                      <PhotoIcon className="h-16 w-16 text-gray-400" />
                    </div>
                  )}
                </div>
                
                <div className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-lg font-medium text-gray-900">{category.name}</h3>
                    {!category.is_active && (
                      <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                        Pasif
                      </span>
                    )}
                  </div>
                  
                  <p className="text-sm text-gray-500 mb-3 line-clamp-2">
                    {category.description || 'Açıklama yok'}
                  </p>
                  
                  <div className="text-xs text-gray-400 mb-4">
                    Oluşturulma: {formatDate(category.created_at)}
                  </div>
                  
                  <div className="flex items-center justify-end space-x-2">
                    <button
                      onClick={() => toggleActive(category)}
                      className={`p-2 rounded-full ${
                        category.is_active 
                          ? 'text-green-600 hover:bg-green-50' 
                          : 'text-gray-400 hover:bg-gray-50'
                      }`}
                      title={category.is_active ? 'Pasif Yap' : 'Aktif Yap'}
                    >
                      {category.is_active ? (
                        <EyeIcon className="h-5 w-5" />
                      ) : (
                        <EyeSlashIcon className="h-5 w-5" />
                      )}
                    </button>
                    <button
                      onClick={() => handleEdit(category)}
                      className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-full"
                      title="Düzenle"
                    >
                      <PencilIcon className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => handleDelete(category.id)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-full"
                      title="Sil"
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border max-w-2xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {editingCategory ? 'Kategoriyi Düzenle' : 'Yeni Kategori Ekle'}
              </h3>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Kategori Adı *</label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                  
                  <div className="flex items-center pt-6">
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                    />
                    <label className="ml-2 block text-sm text-gray-900">
                      Kategori aktif
                    </label>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">Açıklama</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
                
                {/* Görsel Yükleme */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Kategori Görseli</label>
                  
                  {/* Dosya Yükleme */}
                  <div className="mb-4">
                    <label className="cursor-pointer">
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
                        <CloudArrowUpIcon className="mx-auto h-12 w-12 text-gray-400" />
                        <div className="mt-2">
                          <p className="text-sm text-gray-600">
                            <span className="font-semibold">Resim seçmek için tıklayın</span>
                          </p>
                          <p className="text-xs text-gray-500">PNG, JPG, JPEG (MAX. 5MB)</p>
                        </div>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleFileSelect}
                          className="hidden"
                        />
                      </div>
                    </label>
                  </div>

                  {/* Seçili Dosyalar */}
                  {selectedFiles.length > 0 && (
                    <div className="mb-4">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-sm font-medium text-gray-700">Seçili Dosya</h4>
                        <button
                          type="button"
                          onClick={uploadImages}
                          disabled={uploadingImages}
                          className="px-3 py-1 text-sm bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50"
                        >
                          {uploadingImages ? 'Yükleniyor...' : 'Yükle'}
                        </button>
                      </div>
                      <div className="flex gap-2">
                        {selectedFiles.map((file, index) => (
                          <div key={index} className="relative">
                            <div className="w-20 h-20 bg-gray-100 rounded border flex items-center justify-center">
                              <PhotoIcon className="w-8 h-8 text-gray-400" />
                            </div>
                            <button
                              type="button"
                              onClick={() => removeSelectedFile(index)}
                              className="absolute -top-2 -right-2 bg-red-600 text-white rounded-full p-1 hover:bg-red-700"
                            >
                              <XMarkIcon className="w-3 h-3" />
                            </button>
                            <p className="text-xs text-gray-600 mt-1 truncate w-20">{file.name}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Yüklenmiş Görseller */}
                  {uploadedImages.length > 0 && (
                    <div className="mb-4">
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Yüklenmiş Görsel</h4>
                      <div className="flex gap-2">
                        {uploadedImages.map((image, index) => (
                          <div key={index} className="relative">
                            <img
                              src={image.url}
                              alt={`Kategori görseli ${index + 1}`}
                              className="w-20 h-20 object-cover rounded border"
                            />
                            <button
                              type="button"
                              onClick={() => removeUploadedImage(index)}
                              className="absolute -top-2 -right-2 bg-red-600 text-white rounded-full p-1 hover:bg-red-700"
                            >
                              <XMarkIcon className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Manuel URL Girişi */}
                  <div className="border-t pt-4">
                    <label className="block text-sm font-medium text-gray-700">Manuel Resim URL (Opsiyonel)</label>
                    <input
                      type="url"
                      value={formData.image_url}
                      onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                      placeholder="https://example.com/resim.jpg"
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    />
                    {formData.image_url && !uploadedImages.some(img => img.url === formData.image_url) && (
                      <div className="mt-2">
                        <img 
                          src={formData.image_url} 
                          alt="Manuel URL Preview" 
                          className="h-20 w-20 object-cover rounded-md"
                          onError={(e) => {
                            e.target.style.display = 'none'
                          }}
                        />
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="flex justify-end space-x-3 pt-4 border-t">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    İptal
                  </button>
                  <button
                    type="submit"
                    disabled={uploadingImages}
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50"
                  >
                    {uploadingImages ? 'Yükleniyor...' : editingCategory ? 'Güncelle' : 'Ekle'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default CategoriesPage 